import { useCallback, useEffect, useRef, useState } from 'react';
import { router, usePage } from '@inertiajs/react';
import TeacherShell from '../../../components/layout/TeacherShell';
import { Card } from '../../../components/ui';
import VocabActivity from '../../../components/vocabulary/VocabActivity';
import BookReader from '../../../components/reader/BookReader';
import LiveSessionController from '../../../components/live_sessions/LiveSessionController';
import LiveCursorOverlay from '../../../components/live_sessions/LiveCursorOverlay';
import RemoteHighlights from '../../../components/live_sessions/RemoteHighlights';
import RemoteHoverFocus from '../../../components/live_sessions/RemoteHoverFocus';
import PresencePanel from '../../../components/live_sessions/PresencePanel';
import { useHighlightSync } from '../../../components/live_sessions/useHighlightSync';
import { useFocusBroadcast } from '../../../components/live_sessions/useFocusBroadcast';
import { useScrollBroadcast } from '../../../components/live_sessions/useScrollSync';
import {
  topParagraphIndex,
  stepParagraphIndex,
  scrollParagraphToTop,
} from '../../../components/live_sessions/useChapterAnchorSync';
import { usePresenceAway } from '../../../components/live_sessions/usePresenceAway';
import { useLivePresenceChannel } from '../../../hooks/useLivePresenceChannel';

export default function TeacherLiveSessionShow({
  live_session,
  participants,
  invitee,
  activity,
  join_url,
  course_list_options = null,
}) {
  const auth = usePage().props.auth;
  const selfUserId = auth?.user?.id;
  const isChapter = live_session.current_view === 'ChapterReader';
  const readerRef = useRef(null);
  // The teacher drives the shared reader; locked by default for a guided read.
  const [readerLocked, setReaderLocked] = useState(true);
  // The paragraph the session is pinned to ("everyone look here").
  const [pinnedPara, setPinnedPara] = useState(null);
  // Last broadcast chapter + selections + pin, kept so we can re-seed a student
  // who joins mid-session (the broadcasts are one-shot relays they'd miss).
  const lastChapterRef = useRef(null);
  const lastSelectionsRef = useRef(null);
  const lastPinRef = useRef(null);
  const [status, setStatus] = useState(live_session.status);
  // Pass through the `activity` prop directly so partial-reloads triggered
  // by `activity_ready` (or the manual refresh button) immediately surface
  // the new payload. Earlier we cached the initial value via useState,
  // which silently swallowed the refreshed prop and left the teacher
  // stuck on "Waiting for student to join" even after a reload.
  const activityState = activity || null;
  const [cursors, setCursors] = useState({});
  const [highlights, setHighlights] = useState({});
  const [focuses, setFocuses] = useState({}); // user_id -> { key, role, name }
  const [aways, setAways] = useState({}); // user_id -> { away, role, name }
  const [remoteActivityState, setRemoteActivityState] = useState(null);
  // Live ghost_count map for the PresencePanel. Seeded from server-
  // rendered participants prop, updated on the away_state broadcast
  // whose server-side increment fires on the student's away→present.
  const [ghostCounts, setGhostCounts] = useState(() => {
    const seed = {};
    (participants || []).forEach((p) => {
      if (p?.id != null) seed[p.id] = p.ghost_count ?? 0;
    });
    return seed;
  });

  const refreshPresence = () =>
    router.reload({
      only: ['participants', 'invitee'],
      preserveScroll: true,
    });

  const { perform, isConnected } = useLivePresenceChannel(live_session.id, {
    onSessionState: (data) => {
      setStatus(data.status);
      if (Array.isArray(data.participants)) {
        setGhostCounts((prev) => {
          const next = { ...prev };
          data.participants.forEach((p) => {
            if (p?.user_id != null) next[p.user_id] = p.ghost_count ?? 0;
          });
          return next;
        });
      }
    },
    onSessionStatus: (data) => setStatus(data.status),
    // Student's first lobby hit lazily creates the VocabularySession and
    // stores its id in current_view_params. Until that happens the
    // teacher's server-rendered `activity` prop is nil; this signal tells
    // us to refetch it now that the session exists.
    onActivityReady: () => refreshActivity(),
    onPresence: (data) => {
      refreshPresence();
      // Reliability net for the student's initial join. `activity_ready` is a
      // one-shot broadcast fired inside the student's HTTP request; the teacher
      // misses it if their cable wasn't subscribed at that instant (a join
      // race) or after a reconnect, leaving the pane stuck on "Waiting to
      // join". A student's presence connection arrives *after* that request
      // committed the session id, so refetch the activity here when we don't
      // have it yet.
      if (data?.connected && data?.role === 'student' && !activityState) {
        refreshActivity();
      }
      // Seed a freshly-joined student with our current chapter, selections,
      // pin, and lock state (one-shot events they'd otherwise miss).
      if (data?.connected && data?.role === 'student' && isChapter) {
        if (lastChapterRef.current) perform('set_chapter', lastChapterRef.current);
        if (lastSelectionsRef.current) perform('reader_selections', lastSelectionsRef.current);
        perform('reader_lock', { locked: readerLocked });
        if (lastPinRef.current != null) perform('reader_pin', { para: lastPinRef.current });
      }
    },
    onCursor: (data) => {
      setCursors((prev) => ({ ...prev, [data.user_id]: data }));
    },
    onHighlight: (data) => {
      setHighlights((prev) => ({ ...prev, [data.user_id]: data }));
    },
    onProgress: (data) => {
      // Stamp with `id` so OnboardingActivity's effect re-runs on every push,
      // even when the same field comes through twice.
      setRemoteActivityState({ ...(data.state || {}), id: Date.now() });
    },
    onFocus: (data) => {
      setFocuses((prev) => ({
        ...prev,
        [data.user_id]: { key: data.key, role: data.role, name: data.name },
      }));
    },
    onAway: (data) => {
      setAways((prev) => ({
        ...prev,
        [data.user_id]: { away: !!data.away, role: data.role, name: data.name },
      }));
      if (data.ghost_count != null) {
        setGhostCounts((prev) => ({ ...prev, [data.user_id]: data.ghost_count }));
      }
    },
  });

  // Broadcast the teacher's live text selection (like the vocab live session)
  // so the student sees what the teacher is pointing at. We do NOT render
  // RemoteHighlights on the teacher's own side for the chapter view — painting
  // the echoed selection back into the teacher's DOM mutates it mid-drag and
  // breaks the teacher's own highlighting. (The student paints it; see Lobby.)
  useHighlightSync(perform);
  useFocusBroadcast(perform);
  // Teacher drives scroll sync; the channel relays `scroll_sync` to the
  // student, who follows. Gated on a live connection so we don't emit into
  // a dead session. The Chapter Reader uses paragraph-anchor sync instead
  // (below), so the page-level scroll broadcast is disabled for that view.
  useScrollBroadcast(perform, isConnected && !isChapter);
  // Teachers never appear as ghosts to the student — only students get
  // ghost-shamed. The hook still swaps our own favicon while away as a
  // reminder to come back to the lesson.
  usePresenceAway(perform, isConnected, false);

  const refreshActivity = () => {
    router.reload({
      only: ['activity', 'participants', 'invitee', 'live_session'],
      preserveScroll: true,
    });
  };

  // Belt-and-suspenders auto-load. The teacher's activity prop is server-
  // rendered nil until the student's first lobby hit lazily bootstraps the
  // VocabularySession and writes its id into current_view_params. The two
  // event-driven triggers for picking that up — the one-shot `activity_ready`
  // broadcast and the `onPresence` refetch — can BOTH miss it: a join race
  // (teacher's cable not yet subscribed when activity_ready fires), a
  // reconnect, or a stale-closure read of activityState. That left the teacher
  // stranded on "Waiting for student to join" until a manual refresh (more
  // acute for review, whose session build is slower). While we have a student
  // on a practice/onboarding view but no activity yet, poll for it on a short
  // interval and stop the instant it arrives.
  useEffect(() => {
    if (activityState) return undefined;
    if (live_session.current_view !== 'VocabularyPractice') return undefined;
    const hasStudent = (participants || []).some((p) => p.role === 'student');
    if (!hasStudent) return undefined;
    const timer = setInterval(refreshActivity, 2500);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityState, live_session.current_view, participants]);

  const handleActivate = () =>
    router.post(`/teacher/live_sessions/${live_session.id}/activate`);

  const toggleReaderLock = () => {
    setReaderLocked((prev) => {
      const next = !prev;
      perform('reader_lock', { locked: next });
      return next;
    });
  };

  // Pin the session to a paragraph: scroll our own reader to it, mark it, and
  // broadcast so every follower jumps there too.
  const pinTo = useCallback((para) => {
    if (para == null) return;
    setPinnedPara(para);
    lastPinRef.current = para;
    scrollParagraphToTop(readerRef.current, readerRef.current, para, 'smooth');
    perform('reader_pin', { para });
  }, [perform]);

  // Pin to whatever paragraph is currently at the top of the teacher's reader.
  const handlePinHere = useCallback(() => {
    const para = topParagraphIndex(readerRef.current, readerRef.current);
    if (para != null) pinTo(para);
  }, [pinTo]);

  // Step the pin forward/back one paragraph (relative to the current pin, or
  // the current top paragraph if nothing is pinned yet).
  const handlePinStep = useCallback((dir) => {
    const base = pinnedPara != null ? pinnedPara : topParagraphIndex(readerRef.current, readerRef.current);
    const next = stepParagraphIndex(readerRef.current, base, dir);
    pinTo(next);
  }, [pinnedPara, pinTo]);

  // BookReader → broadcast the loaded chapter so the student mirrors it.
  // Reset the pin to the top of the freshly loaded chapter.
  const handleChapterLoaded = useCallback((payload) => {
    lastChapterRef.current = payload;
    perform('set_chapter', payload);
    setPinnedPara(null);
    lastPinRef.current = null;
  }, [perform]);

  const handleSelectionsChanged = useCallback((chapterId, selections) => {
    const msg = { chapter_id: chapterId, selections };
    lastSelectionsRef.current = msg;
    perform('reader_selections', msg);
  }, [perform]);

  const hasActivity =
    !!activityState && live_session.current_view === 'VocabularyPractice';
  const hasChapter = !!activityState && isChapter;

  const isLive = status === 'active' || status === 'draft';
  const scrollTitle = invitee
    ? `Live Session · ${invitee.full_name}`
    : 'Live Session';

  return (
    <TeacherShell
      scrollTitle={scrollTitle}
      forceShowScrollTitle={isLive}
      maxWidth={isChapter ? 'wide' : 'full'}
      className={isChapter ? undefined : '!p-0'}
    >
      {hasChapter ? (
        <div data-activity-root>
          <BookReader
            book={activityState.book}
            chapters={activityState.chapters}
            courseListOptions={course_list_options}
            readerScrollRef={readerRef}
            pinnedParagraphIndex={pinnedPara}
            onChapterLoaded={handleChapterLoaded}
            onSelectionsChanged={handleSelectionsChanged}
          />
        </div>
      ) : hasActivity ? (
        <div data-activity-root>
          <VocabActivity
            {...activityState}
            interactive={false}
            remoteState={remoteActivityState}
            onDone={() => router.visit('/teacher/live_sessions')}
            flagAsTeacherFor={invitee?.id}
            courseListOptions={course_list_options}
            onPeerIntent={(action, payload) => perform('peer_intent', { action, payload })}
          />
        </div>
      ) : status === 'completed' ? (
        <EmptyPanel title="Session ended" body="This live session is over." />
      ) : (
        <EmptyPanel
          title="Waiting for student to join"
          body="Once they accept the invite the activity will appear here."
        />
      )}

      <LiveSessionController
        role="teacher"
        status={status}
        peer={invitee}
        participants={participants}
        joinCode={live_session.join_code}
        joinUrl={join_url}
        hasActivity={hasActivity}
        canActivate={live_session.can_activate}
        onStep={(direction) => perform('step', { direction })}
        onRefresh={refreshActivity}
        onActivate={handleActivate}
        showReaderControls={hasChapter}
        readerLocked={readerLocked}
        onToggleReaderLock={toggleReaderLock}
        pinnedPara={pinnedPara}
        onPinHere={handlePinHere}
        onPinStep={handlePinStep}
      />

      <PresencePanel
        participants={(participants || []).map((p) => ({
          ...p,
          away: !!aways[p.id]?.away,
          ghost_count: ghostCounts[p.id] ?? p.ghost_count ?? 0,
        }))}
        currentUserId={selfUserId}
      />

      <LiveCursorOverlay
        cursors={cursors}
        focuses={focuses}
        aways={aways}
        perform={perform}
        selfUserId={selfUserId}
      />

      {!isChapter && <RemoteHighlights highlights={highlights} selfUserId={selfUserId} />}

      <RemoteHoverFocus focuses={focuses} selfUserId={selfUserId} />
    </TeacherShell>
  );
}

function EmptyPanel({ title, body }) {
  return (
    <Card className="p-12 text-center">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{title}</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400">{body}</p>
    </Card>
  );
}
